import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import { tracked } from '@glimmer/tracking';

import { ContractSignWebSocket } from '@repo/customer-registration-kit/contract-sign';
import { Spinner } from '@repo/design-system-kit';
import { shouldSendToSentry } from '@repo/shared-kit/utils';
import { webviewManager } from '@repo/shared-kit/webview';
import { dropTask } from 'ember-concurrency';
import { variation } from 'ember-launch-darkly';
import window from 'ember-window-mock';

import ENV from 'register-partners-js/config/environment';
import { jsURL, partnerBaseURL } from 'register-partners-js/constants/hosts';
import { MainLayout } from 'register-partners-js/react/components/layouts/main';
import { postRegistrationSubmitAsync } from 'register-partners-js/react/registration/queries';
import { fetchNavigationOrganizations } from 'register-partners-js/utils/fetch-navigation-organizations';
import { ignoreCancelation } from 'register-partners-js/utils/ignore-cancelation';

export default class RegistrationKycController extends Controller {
  mainLayout = MainLayout;
  spinner = Spinner;
  contractSignWebSocket = ContractSignWebSocket;

  @service deviceManager;
  @service intl;
  @service toastFlashMessages;
  @service networkManager;
  @service router;
  @service segment;
  @service sentry;
  @service store;
  @service userManager;

  @tracked contractSignWebSocketEnabled = false;

  get websocketToken() {
    return this.userManager.websocketToken;
  }

  get currentUserId() {
    return this.userManager.currentUser?.id;
  }

  get isOnfido() {
    return this.model.stakeholder.kycProviderName === 'onfido';
  }

  get isFourthline() {
    return this.model.stakeholder.kycProviderName === 'fourthline';
  }

  @action
  onError(error) {
    this.sentry.captureException(error);
    if (error.type === 'expired_token') {
      window.location.reload();
    }
  }

  sendSmsTask = dropTask(async isAutoSendSms => {
    if (isAutoSendSms && ENV.environment === 'test') return;

    try {
      await this.networkManager.request(`${partnerBaseURL}/client_api/sms`, {
        method: 'POST',
        data: JSON.stringify({
          template_name: 'qonto-verification-fourthline',
        }),
      });
      this.toastFlashMessages.toastSuccess(this.intl.t('fourthline.onboarding.sms.success'));
      this.segment.track('onboarding_partner_fourthline_link_sent');
    } catch (error) {
      if (error.status === 429) {
        this.toastFlashMessages.toastError(
          this.intl.t('fourthline.onboarding.sms.error.already-sent')
        );
      } else {
        this.toastFlashMessages.toastError(
          this.intl.t('fourthline.onboarding.sms.error.technical-issue')
        );
      }
    }
  });

  regenerateLinkTask = dropTask(
    waitFor(async () => {
      let { kyc } = this.model;

      if (kyc.reason === 'KYC_API_ERROR') {
        return window.location.reload();
      }

      let stakeholder = await kyc.stakeholder;

      let payload = await this.store.adapterFor('kyc').regenerateKycFromToken(stakeholder.id);

      let { redirect_url, provider_status, is_qes_required, reason } = payload.data.attributes;

      kyc.setProperties({
        redirectUrl: redirect_url,
        providerStatus: provider_status,
        isQesRequired: is_qes_required,
        reason,
      });

      this.segment.track('onboarding_partner_fourthline_regenerate_link_clicked');
    })
  );

  completeTask = dropTask(async () => {
    this.segment.track('onboarding_partner_kyc_succeeded', {
      provider: this.model.stakeholder.kycProviderName,
      is_webview: webviewManager.isWebview,
    });

    if (this.model.stakeholder.kycProviderName === 'onfido') {
      await this.router.transitionTo('registration.kyc-success');
    } else if (this.model.stakeholder.kycProviderName === 'fourthline') {
      if (variation('scaling--async-contract-signature')) {
        await this.submitV2Task.perform().catch(ignoreCancelation);
      } else {
        await this.submitV1Task.perform();
      }
    }
  });

  submitV1Task = dropTask(async () => {
    await this.model.registration.submit();
    await this.redirectAfterSubmit();
  });

  submitV2Task = dropTask(
    waitFor(async () => {
      try {
        await postRegistrationSubmitAsync(this.model.registration.id);
      } catch (error) {
        if (shouldSendToSentry(error)) {
          this.sentry.captureException(error);
        }
        return;
      }

      this.contractSignWebSocketEnabled = true;
    })
  );

  handleContractSignSuccessTask = dropTask(async () => {
    await this.redirectAfterSubmit();
  });

  @action
  handleContractSignValidationError() {
    this.toastFlashMessages.toastError(this.intl.t('errors.internal-server-error'));
  }

  @action
  handleContractSignTimeout() {
    this.router.transitionTo('registration.pending-contract');
  }

  async redirectAfterSubmit() {
    if (this.deviceManager.isMobile) {
      await this.router.transitionTo('registrations.already-created', this.model.organization.id);
    } else {
      let organizations = await fetchNavigationOrganizations(this.networkManager);
      let organization = organizations.find(org => org.id === this.model.organization.id);
      window.location.replace(`${jsURL}/organizations/${organization.slug}`);
    }
  }
}
